import { API_BASE } from "../config";
import { captureFrontendMessage } from "../lib/monitoring";
import { clearClientSession, getAccessToken } from "./session";

const toastListeners = new Set();

function emitToast(payload) {
  toastListeners.forEach((listener) => {
    try {
      listener(payload);
    } catch {}
  });
}

export function onToast(fn) {
  if (typeof fn !== "function") return () => {};
  toastListeners.add(fn);
  return () => toastListeners.delete(fn);
}

export function toastSuccess(message, opts = {}) {
  emitToast({ type: "success", message, ...opts });
}

export function toastError(message, opts = {}) {
  emitToast({ type: "error", message, ...opts });
}

export function toastInfo(message, opts = {}) {
  emitToast({ type: "info", message, ...opts });
}

export function toastWarning(message, opts = {}) {
  emitToast({ type: "warning", message, ...opts });
}

function currentPagePath() {
  if (typeof window === "undefined") return "";
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function createTraceId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export async function authHeaders() {
  const token = await getAccessToken();
  return {
    "Content-Type": "application/json",
    "X-Page-Path": currentPagePath(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export async function authHeadersMultipart() {
  const token = await getAccessToken();
  return {
    "X-Page-Path": currentPagePath(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export async function readJsonSafe(res) {
  try {
    return await res.json();
  } catch {
    return {};
  }
}

function resolveForbiddenError(json) {
  const code = String(json?.code || json?.message || "").trim();

  if (json?.account_status === "suspended" || code === "ACCOUNT_SUSPENDED") {
    return "ACCOUNT_SUSPENDED";
  }

  if (code === "FORBIDDEN_ADMIN_ONLY") return "FORBIDDEN_ADMIN_ONLY";
  if (code === "FORBIDDEN_PERMISSION_DENIED") return "FORBIDDEN_PERMISSION_DENIED";
  if (code === "FORBIDDEN_ADMIN_ACCOUNT_NOT_FOUND") {
    return "FORBIDDEN_ADMIN_ACCOUNT_NOT_FOUND";
  }
  if (code === "FORBIDDEN_ADMIN_ACCOUNT_INACTIVE") {
    return "FORBIDDEN_ADMIN_ACCOUNT_INACTIVE";
  }
  if (code === "SUBSCRIPTION_EXPIRED") return "SUBSCRIPTION_EXPIRED";

  return extractErrorMessage(json, "FORBIDDEN");
}

export function handleAuthStatus(res, json) {
  if (res.status === 401) {
    clearClientSession();
    throw new Error("UNAUTHORIZED");
  }

  if (res.status === 403) {
    const resolved = resolveForbiddenError(json);
    const knownCode =
      resolved === "ACCOUNT_SUSPENDED" ||
      resolved === "FORBIDDEN_ADMIN_ONLY" ||
      resolved === "FORBIDDEN_PERMISSION_DENIED" ||
      resolved === "FORBIDDEN_ADMIN_ACCOUNT_NOT_FOUND" ||
      resolved === "FORBIDDEN_ADMIN_ACCOUNT_INACTIVE" ||
      resolved === "SUBSCRIPTION_EXPIRED";

    if (knownCode) {
      throw new Error(resolved);
    }

    throw buildApiError(json, "FORBIDDEN", res.status);
  }
}

export function extractErrorMessage(json, fallback = "فشل الطلب") {
  if (!json || typeof json !== "object") return fallback;

  const candidates = [json.ui_message, json.message, json.error, json.details, json.detail];

  for (const value of candidates) {
    if (typeof value === "string" && value.trim()) {
      const normalized = value.trim();
      if (/^[A-Z0-9_]+$/.test(normalized)) continue;
      return normalized;
    }
  }

  return fallback;
}

export function buildApiError(json, fallback = "فشل الطلب", status = 0) {
  const error = new Error(extractErrorMessage(json, fallback));
  const code = String(json?.code || "").trim();

  if (code) error.code = code;
  if (Number.isFinite(Number(status)) && Number(status) > 0) {
    error.status = Number(status);
  }

  error.payload = json || {};
  return error;
}

export function buildQueryString(input = {}) {
  const params = new URLSearchParams();

  Object.entries(input).forEach(([key, value]) => {
    if (value == null) return;

    const safeValue = typeof value === "string" ? value.trim() : String(value).trim();
    if (!safeValue) return;

    params.set(key, safeValue);
  });

  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function toPositiveInt(value) {
  const n = Number(value || 0);
  return Number.isInteger(n) && n > 0 ? n : 0;
}

export function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

export function normalizeTagIdsValue(value) {
  if (Array.isArray(value)) {
    return [...new Set(value.map((x) => Number(x)).filter((x) => Number.isInteger(x) && x > 0))];
  }

  const raw = String(value || "").trim();
  if (!raw) return [];

  return [
    ...new Set(
      raw
        .split(",")
        .map((x) => Number(String(x || "").trim()))
        .filter((x) => Number.isInteger(x) && x > 0)
    ),
  ];
}

async function resolveResponse(path, options = {}) {
  const traceId = createTraceId();
  let res;
  try {
    const resolvedHeaders = await Promise.resolve(options.headers || {});
    res = await fetch(`${API_BASE}${path}`, {
      ...options,
      credentials: options.credentials || "include",
      headers: {
        ...resolvedHeaders,
        "X-Trace-Id": traceId,
      },
    });
  } catch (error) {
    captureFrontendMessage("تعذر الاتصال بالخادم", {
      traceId,
      tags: {
        category: "network",
        route: path,
      },
      extra: {
        reason: String(error?.message || error || "network_error"),
      },
      level: "error",
    });

    const networkError = new Error(
      "تعذر الاتصال بالخادم. تأكد من تشغيل الخادم ثم حاول مرة أخرى."
    );
    networkError.traceId = traceId;
    throw networkError;
  }

  const json = await readJsonSafe(res);
  handleAuthStatus(res, json);

  if (!res.ok) {
    const error = buildApiError(json, "فشل الطلب", res.status);
    error.traceId = res.headers.get("x-trace-id") || traceId;

    captureFrontendMessage(error.message, {
      traceId: error.traceId,
      tags: {
        category: "api",
        status_code: String(res.status),
        route: path,
      },
      extra: {
        payload: json,
      },
      level: res.status >= 500 ? "error" : "warning",
    });

    throw error;
  }

  return json;
}

export async function apiGet(path) {
  return resolveResponse(path, {
    headers: authHeaders(),
  });
}

export async function apiPost(path, body = {}) {
  return resolveResponse(path, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body || {}),
  });
}

export async function apiPut(path, body = {}) {
  return resolveResponse(path, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(body || {}),
  });
}

export async function apiDelete(path) {
  return resolveResponse(path, {
    method: "DELETE",
    headers: authHeaders(),
  });
}

export { API_BASE };
